/**
 * Log Viewer Page - Modern UI for viewing/downloading system logs
 * Master role only
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Fade,
  LinearProgress,
  Divider,
  Card,
  CardContent,
  Grid,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
  Close as CloseIcon,
  Article as LogIcon,
  Storage as StorageIcon,
  FolderOpen as FolderIcon,
  Schedule as ScheduleIcon,
  KeyboardArrowDown as MoreIcon,
  FilterList as FilterIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { formatDistanceToNow } from 'date-fns';
import { vi, enUS, ja } from 'date-fns/locale';
import api from '../services/api';

const API_BASE = '/api/v1/master/logs';

export default function LogViewerPage() {
  const theme = useTheme();
  const { t, i18n } = useTranslation(['common', 'logs']);
  
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Viewer state
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerLoading, setViewerLoading] = useState(false);
  const [viewerData, setViewerData] = useState(null);
  const [viewerSearch, setViewerSearch] = useState('');
  const [loadingMore, setLoadingMore] = useState(false);
  
  const contentRef = useRef(null);

  const currentLocale = (() => {
    const lang = (i18n.resolvedLanguage || i18n.language || 'vi').split('-')[0];
    if (lang === 'en') return enUS;
    if (lang === 'ja') return ja;
    return vi;
  })();

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [logsRes, statsRes] = await Promise.all([
        api.get(API_BASE, { params: { search: searchTerm || undefined } }),
        api.get(`${API_BASE}/stats`)
      ]);
      setLogs(logsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Không thể tải danh sách log');
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleViewLog = async (filePath) => {
    try {
      setViewerOpen(true);
      setViewerLoading(true);
      setViewerData(null);
      setViewerSearch('');
      
      const res = await api.get(`${API_BASE}/read`, {
        params: { file_path: filePath, lines: 500, offset: 0 }
      });
      setViewerData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Không thể đọc file log');
      setViewerOpen(false);
    } finally {
      setViewerLoading(false);
    }
  };

  const handleLoadMore = async () => {
    if (!viewerData || loadingMore || !viewerData.has_more) return;
    
    try {
      setLoadingMore(true);
      const res = await api.get(`${API_BASE}/read`, {
        params: {
          file_path: viewerData.file_path,
          lines: 500,
          offset: viewerData.offset + viewerData.lines,
          search: viewerSearch || undefined
        }
      });
      
      setViewerData(prev => ({
        ...res.data,
        content: prev.content + res.data.content,
        offset: prev.offset,
        lines: prev.lines + res.data.lines,
      }));
    } catch (err) {
      console.error('Load more error:', err);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleSearchInFile = async () => {
    if (!viewerData) return;
    
    try {
      setViewerLoading(true);
      const res = await api.get(`${API_BASE}/read`, {
        params: {
          file_path: viewerData.file_path,
          lines: 500,
          offset: 0,
          search: viewerSearch || undefined
        }
      });
      setViewerData(res.data);
    } catch (err) {
      console.error('Search error:', err);
    } finally {
      setViewerLoading(false);
    }
  };

  const handleDownload = async (filePath) => {
    try {
      const res = await api.get(`${API_BASE}/download`, {
        params: { file_path: filePath },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filePath.split('/').pop());
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Không thể tải file');
    }
  };

  const handleCopyContent = () => {
    if (viewerData?.content) {
      navigator.clipboard.writeText(viewerData.content);
    }
  };

  const getLogLevelColor = (line) => {
    if (line.includes('ERROR') || line.includes('CRITICAL')) return theme.palette.error.main;
    if (line.includes('WARNING') || line.includes('WARN')) return theme.palette.warning.main;
    if (line.includes('INFO')) return theme.palette.info.main;
    if (line.includes('DEBUG')) return theme.palette.grey[500];
    return theme.palette.text.primary;
  };

  const formatLogContent = (content) => {
    if (!content) return null;
    
    return content.split('\n').map((line, idx) => (
      <Box
        key={idx}
        component="span"
        sx={{
          display: 'block',
          color: getLogLevelColor(line),
          py: 0.25,
          px: 1,
          fontFamily: 'monospace',
          fontSize: '0.8rem',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-all',
          '&:hover': {
            bgcolor: alpha(theme.palette.primary.main, 0.08),
          },
        }}
      >
        <Box
          component="span"
          sx={{
            color: theme.palette.grey[500],
            mr: 2,
            minWidth: 50,
            display: 'inline-block',
            userSelect: 'none',
          }}
        >
          {idx + 1}
        </Box>
        {line}
      </Box>
    ));
  };

  return (
    <Box sx={{ height: '100%', overflow: 'auto', p: 3, bgcolor: '#f5f7fa' }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
            }}
          >
            <LogIcon sx={{ color: 'white', fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={700}>
              {t('logs:title', 'Log Viewer')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('logs:subtitle', 'Xem và tải xuống log hệ thống')}
            </Typography>
          </Box>
        </Box>

        {/* Stats Cards */}
        {stats && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 1.5,
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <LogIcon sx={{ color: theme.palette.primary.main }} />
                  </Box>
                  <Box>
                    <Typography variant="h6" fontWeight={700}>
                      {stats.total_files}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('logs:totalFiles', 'Tổng file log')}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 1.5,
                      bgcolor: alpha(theme.palette.success.main, 0.1),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <StorageIcon sx={{ color: theme.palette.success.main }} />
                  </Box>
                  <Box>
                    <Typography variant="h6" fontWeight={700}>
                      {stats.total_size_formatted}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('logs:totalSize', 'Tổng dung lượng')}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 1.5,
                      bgcolor: alpha(theme.palette.warning.main, 0.1),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <FolderIcon sx={{ color: theme.palette.warning.main }} />
                  </Box>
                  <Box>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{
                        maxWidth: 200,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {stats.log_dir}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('logs:logDirectory', 'Thư mục log')}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Search & Actions */}
        <Paper sx={{ p: 2, borderRadius: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder={t('logs:searchPlaceholder', 'Tìm kiếm file log...')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && fetchLogs()}
            sx={{ flexGrow: 1, minWidth: 200 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchLogs}
            disabled={loading}
          >
            {t('logs:refresh', 'Làm mới')}
          </Button>
        </Paper>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2, borderRadius: 2 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Logs Table */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        {loading && <LinearProgress />}
        <TableContainer sx={{ maxHeight: 'calc(100vh - 400px)' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, bgcolor: '#f8f9fa' }}>
                  {t('logs:fileName', 'Tên file')}
                </TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: '#f8f9fa' }}>
                  {t('logs:size', 'Dung lượng')}
                </TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: '#f8f9fa' }}>
                  {t('logs:modified', 'Cập nhật')}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, bgcolor: '#f8f9fa' }}>
                  {t('logs:actions', 'Thao tác')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.length === 0 && !loading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                    <LogIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography color="text.secondary">
                      {t('logs:noLogs', 'Không có file log nào')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log, idx) => (
                  <Fade in key={log.path} timeout={200 + idx * 50}>
                    <TableRow
                      hover
                      sx={{
                        '&:hover': {
                          bgcolor: alpha(theme.palette.primary.main, 0.04),
                        },
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LogIcon fontSize="small" color="action" />
                          <Box>
                            <Typography variant="body2" fontWeight={500}>
                              {log.name}
                            </Typography>
                            {log.path !== log.name && (
                              <Typography variant="caption" color="text.secondary">
                                {log.path}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={log.size_formatted}
                          sx={{
                            fontWeight: 500,
                            bgcolor: alpha(theme.palette.info.main, 0.1),
                            color: theme.palette.info.dark,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <ScheduleIcon fontSize="small" color="action" />
                          <Typography variant="body2" color="text.secondary">
                            {formatDistanceToNow(new Date(log.modified_at), {
                              addSuffix: true,
                              locale: currentLocale,
                            })}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title={t('logs:view', 'Xem')}>
                          <IconButton
                            size="small"
                            onClick={() => handleViewLog(log.path)}
                            sx={{ color: theme.palette.primary.main }}
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('logs:download', 'Tải xuống')}>
                          <IconButton
                            size="small"
                            onClick={() => handleDownload(log.path)}
                            sx={{ color: theme.palette.success.main }}
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  </Fade>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Log Viewer Dialog */}
      <Dialog
        open={viewerOpen}
        onClose={() => setViewerOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            height: '90vh',
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: 1,
            borderColor: 'divider',
            py: 1.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LogIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>
              {viewerData?.file_path || 'Log Viewer'}
            </Typography>
            {viewerData && (
              <Chip
                size="small"
                label={`${viewerData.total_lines} dòng`}
                sx={{ ml: 1 }}
              />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Copy nội dung">
              <IconButton size="small" onClick={handleCopyContent}>
                <CopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Tải xuống">
              <IconButton
                size="small"
                onClick={() => viewerData && handleDownload(viewerData.file_path)}
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <IconButton size="small" onClick={() => setViewerOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Tìm kiếm trong file..."
            value={viewerSearch}
            onChange={(e) => setViewerSearch(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearchInFile()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FilterIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: viewerSearch && (
                <InputAdornment position="end">
                  <Button size="small" onClick={handleSearchInFile}>
                    Tìm
                  </Button>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <DialogContent
          ref={contentRef}
          sx={{
            p: 0,
            bgcolor: '#1e1e1e',
            overflow: 'auto',
            '&::-webkit-scrollbar': {
              width: 8,
              height: 8,
            },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: 'rgba(255,255,255,0.2)',
              borderRadius: 4,
            },
          }}
        >
          {viewerLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
              <CircularProgress />
            </Box>
          ) : viewerData?.content ? (
            <Box sx={{ color: '#d4d4d4' }}>
              {formatLogContent(viewerData.content)}
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 10, color: '#888' }}>
              Không có nội dung
            </Box>
          )}
        </DialogContent>

        {viewerData?.has_more && (
          <DialogActions sx={{ justifyContent: 'center', py: 1, borderTop: 1, borderColor: 'divider' }}>
            <Button
              onClick={handleLoadMore}
              disabled={loadingMore}
              startIcon={loadingMore ? <CircularProgress size={16} /> : <MoreIcon />}
            >
              {loadingMore ? 'Đang tải...' : 'Tải thêm'}
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </Box>
  );
}
